import type {
  PortalVersion,
  VersionAction,
  VersionPlan,
  VersionSwitchResult,
  VersionsView,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, Ban, Info, RotateCcw } from 'lucide-react';
import { useId, useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Textarea } from '../../ui/form';
import { KeyValue, Panel } from '../../ui/layout';

const KIND: Record<VersionAction['kind'], string> = {
  deploy: 'Выкладка',
  rollback: 'Возврат на прошлую версию',
  return: 'Возврат вперёд',
  backup: 'Копия базы',
};

const STATUS: Record<string, { text: string; tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info' }> =
  {
    scheduled: { text: 'назначено', tone: 'info' },
    running: { text: 'выполняется', tone: 'info' },
    started: { text: 'запущено в Railway', tone: 'success' },
    done: { text: 'готово', tone: 'success' },
    failed: { text: 'не удалось', tone: 'danger' },
    cancelled: { text: 'отменено', tone: 'neutral' },
    skipped: { text: 'пропущено', tone: 'neutral' },
  };

const BACKUP_MODE = {
  off: 'не делается',
  try: 'перед каждой миграцией; если не получилась — выкладка продолжается',
  required: 'перед каждой миграцией; без копии миграция не начнётся',
} as const;

function Notes({ items, tone }: { items: string[]; tone: 'danger' | 'info' }) {
  if (!items.length) return null;
  const Icon = tone === 'danger' ? Ban : Info;
  return (
    <ul
      className={clsx(
        'flex flex-col gap-1 rounded-md px-3 py-2.5 text-[13px]',
        tone === 'danger' ? 'bg-danger-soft text-danger' : 'bg-info-soft text-info',
      )}
    >
      {items.map((t) => (
        <li key={t} className="flex gap-1.5">
          <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span className="[overflow-wrap:anywhere]">{t}</span>
        </li>
      ))}
    </ul>
  );
}

/** Переключение на выбранную версию: проверка совместимости с базой, причина, через 5 минут или сразу */
function SwitchDialog({ version, onClose }: { version: PortalVersion; onClose: () => void }) {
  const qc = useQueryClient();
  const reasonId = useId();
  const [reason, setReason] = useState('');
  const [delay, setDelay] = useState<0 | 5>(5);
  const [confirm, setConfirm] = useState(false);
  const plan = useQuery({
    queryKey: ['platform', 'versions', 'plan', version.commit],
    queryFn: () => api<VersionPlan>('/platform/versions/plan', { body: { commit: version.commit } }),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const run = useMutation({
    mutationFn: () =>
      api<VersionSwitchResult>('/platform/versions/switch', {
        body: { commit: version.commit, reason: reason.trim(), delayMinutes: delay, confirm },
      }),
    onSuccess: (r) => {
      toast(
        delay
          ? `Переключение назначено на ${dateTime(r.startsAt)} — пользователи увидят предупреждение`
          : 'Переключение запущено — через 1–3 минуты портал будет на выбранной версии',
      );
      void qc.invalidateQueries({ queryKey: ['platform', 'versions'] });
      onClose();
    },
  });
  const p = plan.data;
  const reasonOk = reason.trim().length >= 5;
  const canRun = Boolean(p?.ok) && reasonOk && (p?.compatibility !== 'warn' || confirm);
  return (
    <Dialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={p?.direction === 'forward' ? 'Вернуться вперёд' : 'Вернуть прошлую версию'}
      description={`${version.shortCommit} от ${dateTime(version.at)}${version.message ? ` — ${version.message}` : ''}`}
      width="lg"
      footer={
        <>
          <Button onClick={onClose}>Закрыть</Button>
          <Button variant="danger" onClick={() => run.mutate()} loading={run.isPending} disabled={!canRun}>
            {delay ? 'Переключить через 5 минут' : 'Переключить сейчас'}
          </Button>
        </>
      }
    >
      {plan.isPending ? (
        <Spinner label="Проверка совместимости" />
      ) : plan.isError ? (
        <ErrorBox error={plan.error} />
      ) : p ? (
        <div className="flex flex-col gap-4">
          <p className="text-[14px] text-ink">
            Портал переключится на программу этой версии: тот же собранный образ, без новой сборки. Данные в
            базе не меняются — всё, что введено после выкладки, останется.
          </p>
          <Notes items={p.blocks} tone="danger" />
          {p.migrations.length ? (
            <section className="flex flex-col gap-2">
              <h3 className="text-[14px] font-semibold text-ink">
                Миграции базы новее этой версии — она будет работать с ними
              </h3>
              <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
                {p.migrations.map((m) => (
                  <li key={m.tag} className="flex flex-col gap-1 px-3 py-2 text-[13px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink">{m.tag}</span>
                      <Badge tone={m.risk === 'ok' ? 'success' : m.risk === 'warn' ? 'warning' : 'danger'}>
                        {m.risk === 'ok' ? 'совместима' : m.risk === 'warn' ? 'проверьте' : 'несовместима'}
                      </Badge>
                    </div>
                    {m.reasons.length ? (
                      <ul className="list-disc pl-5 text-ink-2">
                        {m.reasons.map((r) => (
                          <li key={r}>{r}</li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {p.compatibility === 'warn' ? (
            <div className="flex flex-col gap-2 rounded-md bg-warning-soft px-3 py-2.5 text-[13px] text-warning">
              <p className="flex gap-1.5">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                Миграции меняли данные: прошлая версия может показывать часть записей не так, как новая.
              </p>
              <Checkbox
                label="Понимаю, переключить всё равно"
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
              />
            </div>
          ) : null}
          <Notes items={p.notes} tone="info" />
          {p.services.length ? (
            <p className="text-[13px] text-ink-2">
              Сервисы:{' '}
              {p.services
                .map((s) => `${s.name} — ${s.deploymentId ? `выкладка ${dateTime(s.at)}` : 'нет выкладки'}`)
                .join('; ')}
            </p>
          ) : null}
          {p.ok ? (
            <>
              <fieldset className="flex flex-col gap-2">
                <legend className="mb-1 text-[13px] font-medium text-ink">Когда</legend>
                <label className="flex min-h-11 cursor-pointer items-start gap-2.5 md:min-h-0">
                  <input
                    type="radio"
                    name="delay"
                    className="mt-1"
                    checked={delay === 5}
                    onChange={() => setDelay(5)}
                  />
                  <span className="text-[14px] text-ink">
                    Через 5 минут — пользователи увидят предупреждение и успеют сохранить работу
                  </span>
                </label>
                <label className="flex min-h-11 cursor-pointer items-start gap-2.5 md:min-h-0">
                  <input
                    type="radio"
                    name="delay"
                    className="mt-1"
                    checked={delay === 0}
                    onChange={() => setDelay(0)}
                  />
                  <span className="text-[14px] text-ink">Сразу — если портал сейчас работает с ошибкой</span>
                </label>
              </fieldset>
              <Field
                label="Причина"
                htmlFor={reasonId}
                hint="Видна в истории версий"
                error={reason && !reasonOk ? 'Опишите причину — хотя бы несколько слов' : undefined}
              >
                <Textarea
                  id={reasonId}
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Например: после выкладки не открываются заявки на оплату"
                />
              </Field>
            </>
          ) : null}
          {run.error ? <ErrorBox error={run.error} /> : null}
        </div>
      ) : null}
    </Dialog>
  );
}

/**
 * Версии портала у владельца портала (НФ-21): работающая версия, история выкладок, возврат на прошлую версию и
 * обратно, копия базы перед миграциями и по кнопке.
 */
export function VersionsPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['platform', 'versions'],
    queryFn: () => api<VersionsView>('/platform/versions'),
  });
  const [pick, setPick] = useState<PortalVersion | null>(null);
  const backup = useMutation({
    mutationFn: () =>
      api<{ status: string; detail: string | null }>('/platform/versions/backup', { method: 'POST' }),
    onSuccess: (r) => {
      toast(r.detail ?? 'Копия базы сделана', r.status === 'failed' ? 'danger' : 'success');
      void qc.invalidateQueries({ queryKey: ['platform', 'versions'] });
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/platform/versions/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast('Переключение отменено');
      void qc.invalidateQueries({ queryKey: ['platform', 'versions'] });
    },
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const v = q.data;
  const scheduled = v.actions.filter((a) => a.status === 'scheduled');
  const behind = v.current.dbMigrations > v.current.codeMigrations;
  return (
    <div className="flex flex-col gap-6">
      {scheduled.map((a) => (
        <div
          key={a.id}
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-info-soft px-4 py-3 text-[14px] text-info"
        >
          <span>
            Назначено: {KIND[a.kind].toLowerCase()} на {a.commit?.slice(0, 7)} —{' '}
            {typeof a.details.startsAt === 'string' ? dateTime(a.details.startsAt) : 'скоро'}
            {a.actor ? `, назначил ${a.actor.fullName}` : ''}
          </span>
          <Button size="sm" onClick={() => cancel.mutate(a.id)} loading={cancel.isPending}>
            Отменить переключение
          </Button>
        </div>
      ))}

      <Panel title="Работающая версия" meta="НФ-21">
        <div className="flex flex-col gap-4">
          <KeyValue
            items={[
              { label: 'Версия', value: <span className="num">{v.current.version}</span> },
              { label: 'Коммит', value: <span className="num">{v.current.commit.slice(0, 7)}</span> },
              { label: 'Запущена', value: <span className="num">{dateTime(v.current.startedAt)}</span> },
              {
                label: 'Миграции базы',
                value: (
                  <span>
                    в базе {v.current.dbMigrations}, знает версия {v.current.codeMigrations}
                    {v.current.lastMigration ? ` (последняя ${v.current.lastMigration})` : ''}
                  </span>
                ),
              },
              {
                label: 'Копия базы',
                value: (
                  <span>
                    {BACKUP_MODE[v.backup.mode]}
                    {v.backup.last
                      ? `. Последняя: ${dateTime(v.backup.last.at)} — ${STATUS[v.backup.last.status]?.text ?? v.backup.last.status}`
                      : ''}
                  </span>
                ),
              },
            ]}
          />
          {behind ? (
            <p className="rounded-md bg-warning-soft px-3 py-2.5 text-[13px] text-warning">
              В базе миграций больше, чем знает работающая версия: портал работает на прошлой версии после
              возврата. Новые миграции остаются в базе, данные не потеряны.
            </p>
          ) : null}
          {!v.railway.configured || v.railway.detail ? (
            <p className="rounded-md bg-surface-2 px-3 py-2.5 text-[13px] text-ink-2">
              {v.railway.detail ?? 'Ключ Railway не задан'}. Чтобы возвращать версии отсюда, в переменных
              сервиса api задайте RAILWAY_API_TOKEN — ключ проекта Railway на окружение production; для копии
              тома базы — ещё RAILWAY_DB_VOLUME_INSTANCE_ID и PRE_MIGRATION_BACKUP=try. Без ключа вернуть
              версию можно в Railway: выкладка сервиса → Rollback.
            </p>
          ) : null}
          {v.backup.configured && v.railway.configured ? (
            <div>
              <Button onClick={() => backup.mutate()} loading={backup.isPending}>
                Сделать копию базы сейчас
              </Button>
            </div>
          ) : null}
          {backup.error ? <ErrorBox error={backup.error} /> : null}
        </div>
      </Panel>

      <Panel title="Версии" meta="новые сверху" bodyClassName="p-0">
        {!v.versions.length ? (
          <div className="p-5">
            <EmptyState title="Версий пока нет">
              Версии появляются при выкладке: мигратор записывает коммит и применённые миграции.
            </EmptyState>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {v.versions.map((ver) => (
              <li
                key={ver.commit}
                className="flex flex-col gap-2 px-5 py-3 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="num font-semibold text-ink">{ver.shortCommit}</span>
                    <span className="num text-[13px] text-ink-2">{dateTime(ver.at)}</span>
                    {ver.current ? <Badge tone="success">работает</Badge> : null}
                    {ver.newer ? <Badge tone="info">новее работающей</Badge> : null}
                  </div>
                  {ver.message ? (
                    <div className="text-[14px] [overflow-wrap:anywhere] text-ink">{ver.message}</div>
                  ) : null}
                  <div className="text-[13px] [overflow-wrap:anywhere] text-ink-2">
                    {ver.migrationsApplied.length
                      ? `Миграции: ${ver.migrationsApplied.join(', ')}`
                      : 'Без новых миграций'}
                    {ver.services.some((s) => s.status)
                      ? ` · ${ver.services.map((s) => `${s.name}: ${s.status ?? 'нет'}`).join(', ')}`
                      : ''}
                  </div>
                </div>
                {ver.switchable ? (
                  <Button
                    size="sm"
                    variant={ver.newer ? 'secondary' : 'danger'}
                    icon={<RotateCcw className="size-4" aria-hidden />}
                    onClick={() => setPick(ver)}
                  >
                    {ver.newer ? 'Вернуться к этой версии' : 'Вернуть эту версию'}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Что делали с версиями" bodyClassName="p-0">
        {!v.actions.length ? (
          <div className="p-5">
            <EmptyState title="Пока ничего">
              Здесь будут возвраты версий и копии базы: кто, когда и почему.
            </EmptyState>
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {v.actions.map((a) => {
              const st = STATUS[a.status] ?? { text: a.status, tone: 'neutral' as const };
              const error = typeof a.details.error === 'string' ? a.details.error : null;
              return (
                <li key={a.id} className="flex flex-col gap-0.5 px-5 py-3 text-[14px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-ink">{KIND[a.kind]}</span>
                    {a.commit ? <span className="num text-ink-2">{a.commit.slice(0, 7)}</span> : null}
                    <Badge tone={st.tone}>{st.text}</Badge>
                  </div>
                  <div className="text-[13px] text-ink-2">
                    <span className="num">{dateTime(a.at)}</span>
                    {a.actor ? ` · ${a.actor.fullName}` : ''}
                    {a.reason ? ` · ${a.reason}` : ''}
                  </div>
                  {error ? <div className="text-[13px] text-danger">{error}</div> : null}
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      {pick ? <SwitchDialog key={pick.commit} version={pick} onClose={() => setPick(null)} /> : null}
    </div>
  );
}
